import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { authClient } from '../lib/auth-client'

interface SessionUser {
  id: string
  email: string
  name?: string
  image?: string | null
}

interface SessionData {
  user: SessionUser
  session: { id: string; activeOrganizationId?: string | null }
}

export const useAuthStore = defineStore('auth', () => {
  const session = ref<SessionData | null>(null)
  const ready = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const isAuthenticated = computed(() => session.value !== null)
  const activeOrgId = computed(() => session.value?.session.activeOrganizationId ?? null)

  async function init() {
    if (ready.value) return
    try {
      const { data } = await authClient.getSession()
      session.value = (data as SessionData | null) ?? null
      if (session.value && !session.value.session.activeOrganizationId) {
        await ensureActiveOrganization()
      }
    } catch {
      session.value = null
    } finally {
      ready.value = true
    }
  }

  async function ensureActiveOrganization() {
    try {
      const { data: orgs } = await authClient.organization.list()
      const list = (orgs ?? []) as Array<{ id: string }>
      const first = list[0]
      if (!first) return
      await authClient.organization.setActive({ organizationId: first.id })
      const { data } = await authClient.getSession()
      session.value = (data as SessionData | null) ?? null
    } catch {
      // ignore - onboarding will be shown
    }
  }

  async function signIn(email: string, password: string) {
    loading.value = true
    error.value = null
    try {
      const { error: e } = await authClient.signIn.email({ email, password })
      if (e) throw new Error(e.message ?? 'Sign-in failed')
      const { data } = await authClient.getSession()
      session.value = (data as SessionData | null) ?? null
    } catch (e) {
      error.value = (e as Error).message
      throw e
    } finally {
      loading.value = false
    }
  }

  async function signUp(email: string, password: string, name: string) {
    loading.value = true
    error.value = null
    try {
      const { error: e } = await authClient.signUp.email({ email, password, name })
      if (e) throw new Error(e.message ?? 'Sign-up failed')
      const { data } = await authClient.getSession()
      session.value = (data as SessionData | null) ?? null
    } catch (e) {
      error.value = (e as Error).message
      throw e
    } finally {
      loading.value = false
    }
  }

  async function signOut() {
    await authClient.signOut()
    session.value = null
  }

  async function setActiveOrganization(organizationId: string) {
    await authClient.organization.setActive({ organizationId })
    const { data } = await authClient.getSession()
    session.value = (data as SessionData | null) ?? null
  }

  return {
    session,
    ready,
    loading,
    error,
    isAuthenticated,
    activeOrgId,
    init,
    signIn,
    signUp,
    signOut,
    setActiveOrganization,
  }
})
